import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogIn, Loader2, Mail, Lock, AlertCircle } from 'lucide-react';
import { authApi } from '@/lib/api';

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await authApi.login(email, password);
      if (res.success) {
        navigate(authApi.isAdmin() ? '/admin' : authApi.isExhibitor() ? '/exhibitor' : '/');
      } else {
        setError(res.message || 'Login failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-950 via-slate-900 to-emerald-950 p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 text-sm mb-6">
          ← Back to Exhibition
        </Link>
        
        <div className="bg-slate-900/70 backdrop-blur-xl rounded-3xl p-8 border border-emerald-500/20 shadow-2xl">
          <div className="text-center mb-6">
            <div className="text-4xl mb-2">🌿</div>
            <h1 className="text-white text-2xl font-black">Welcome Back</h1>
            <p className="text-slate-400 text-sm mt-1">Sign in to Hari Landskap Negara 2026</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm" data-testid="login-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label className="text-slate-300 text-xs font-bold uppercase tracking-wider mb-1.5 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                  placeholder="you@example.com"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <label className="text-slate-300 text-xs font-bold uppercase tracking-wider mb-1.5 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold transition-all shadow-lg shadow-emerald-500/30 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><LogIn className="w-5 h-5" /> Sign In</>}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-800 text-center text-sm">
            <span className="text-slate-400">Don&apos;t have an account? </span>
            <Link to="/register" className="text-emerald-400 hover:text-emerald-300 font-bold" data-testid="link-to-register">Register here</Link>
          </div>

          <div className="mt-4 p-3 rounded-lg bg-slate-800/50 border border-slate-700/50 text-xs text-slate-400">
            <div className="font-bold text-slate-300 mb-1">Demo Admin:</div>
            <div>admin@landskap.gov.my / password123</div>
          </div>
        </div>
      </div>
    </div>
  );
}
